import { useMemo, useState } from 'react';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { appColors } from '@base/useTheme';
import { DatasetRepresentation } from '@common/api';
import { useDataset, useDatasets, useDatasetVersions, useDeleteDataset, usePromoteVersion } from './useDatasets';
import { useDatasetPreview } from './useDatasetPreview';
import { DataTable } from './data-table/DataTable';
import {
    DatasetListTable,
    DatasetListTh,
    DatasetListTd,
    DatasetRow,
    FormatBadge,
    DetailHeader,
    BackButton,
    SectionLabel,
    EmptyState,
    ActionButton,
    VersionBadge,
} from './DatasetCatalog.style';

function formatBytes(bytes: number | null | undefined): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(date: Date | null | undefined): string {
    if (!date) return '—';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

// ── List View ────────────────────────────────────────────────────────

function DatasetListView({
    datasets,
    onSelect,
}: {
    datasets: DatasetRepresentation[];
    onSelect: (dataset: DatasetRepresentation) => void;
}) {
    const [sortBy, setSortBy] = useState<'name' | 'updatedAt'>('name');

    const sorted = useMemo(
        () =>
            [...datasets].sort((a, b) => {
                if (sortBy === 'updatedAt') return b.updatedAt.getTime() - a.updatedAt.getTime();
                return a.name.localeCompare(b.name);
            }),
        [datasets, sortBy]
    );

    if (datasets.length === 0) {
        return <EmptyState>No datasets yet. Datasets are created when pipelines run with dataset storage.</EmptyState>;
    }

    return (
        <DatasetListTable>
            <thead>
                <tr>
                    <DatasetListTh onClick={() => setSortBy('name')}>Name {sortBy === 'name' ? '↑' : ''}</DatasetListTh>
                    <DatasetListTh>Format</DatasetListTh>
                    <DatasetListTh onClick={() => setSortBy('updatedAt')}>
                        Last updated {sortBy === 'updatedAt' ? '↓' : ''}
                    </DatasetListTh>
                </tr>
            </thead>
            <tbody>
                {sorted.map((dataset) => (
                    <DatasetRow key={dataset.id} onClick={() => onSelect(dataset)}>
                        <DatasetListTd style={{ fontWeight: 500 }}>{dataset.name}</DatasetListTd>
                        <DatasetListTd>
                            <FormatBadge>{dataset.format}</FormatBadge>
                        </DatasetListTd>
                        <DatasetListTd title={dataset.updatedAt.toLocaleString()}>
                            {timeAgo(dataset.updatedAt)}
                        </DatasetListTd>
                    </DatasetRow>
                ))}
            </tbody>
        </DatasetListTable>
    );
}

// ── Detail View ──────────────────────────────────────────────────────

function DatasetDetailView({
    dataset: datasetSnapshot,
    onBack,
}: {
    dataset: DatasetRepresentation;
    onBack: () => void;
}) {
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
    const { data: liveDataset } = useDataset(datasetSnapshot.id);
    const dataset = liveDataset ?? datasetSnapshot;
    const { data: versions, isLoading: versionsLoading } = useDatasetVersions(dataset.id);
    const deleteDataset = useDeleteDataset();
    const promoteVersion = usePromoteVersion(dataset.id);

    const effectiveVersionId = selectedVersionId ?? dataset.currentVersionId ?? null;
    const {
        data: preview,
        isLoading: previewLoading,
        error: previewError,
        hasMore,
        loadMore,
        fileCount,
        previewedFilename,
    } = useDatasetPreview(effectiveVersionId ? dataset.id : null, effectiveVersionId, dataset.format);

    const handleDelete = () => {
        if (window.confirm(`Delete dataset "${dataset.name}"? This removes all versions and cannot be undone.`)) {
            deleteDataset.mutate(dataset.id, { onSuccess: onBack });
        }
    };

    const handlePromote = (versionId: string) => {
        const short = versionId.substring(0, 12);
        if (window.confirm(`Promote version ${short} to current? Subsequent reads will resolve to this version.`)) {
            promoteVersion.mutate(versionId);
        }
    };

    return (
        <FlexCol $gap={16}>
            <DetailHeader>
                <FlexRow $align="center" $gap={12}>
                    <BackButton onClick={onBack}>Datasets</BackButton>
                    <span style={{ color: appColors.neutral.w300 }}>/</span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{dataset.name}</span>
                    <FormatBadge>{dataset.format}</FormatBadge>
                </FlexRow>
                <ActionButton $danger onClick={handleDelete}>
                    Delete
                </ActionButton>
            </DetailHeader>

            {dataset.description && (
                <p style={{ margin: 0, color: appColors.neutral.w600, fontSize: 13 }}>{dataset.description}</p>
            )}

            {/* Data Preview */}
            <FlexCol $gap={8}>
                <SectionLabel>
                    Data Preview
                    {preview?.totalRows != null && ` (${preview.totalRows.toLocaleString()} rows)`}
                </SectionLabel>
                {fileCount > 1 && previewedFilename && (
                    <div style={{ color: appColors.neutral.w600, fontSize: 12 }}>
                        This version has {fileCount} files — showing <code>{previewedFilename}</code> only.
                    </div>
                )}
                {!dataset.currentVersionId && !selectedVersionId && (
                    <EmptyState>No data yet — this dataset has no completed versions.</EmptyState>
                )}
                {!preview && previewLoading && (
                    <div style={{ padding: 16, color: appColors.neutral.w500, fontSize: 12 }}>Loading preview...</div>
                )}
                {previewError instanceof Error && (
                    <div style={{ padding: 16, color: appColors.error.w500, fontSize: 12 }}>
                        Preview failed: {previewError.message}
                    </div>
                )}
                {preview && preview.columns.length > 0 && (
                    <DataTable
                        columns={preview.columns}
                        rows={preview.rows}
                        totalRows={preview.totalRows ?? undefined}
                        hasMore={hasMore}
                        onLoadMore={hasMore ? loadMore : undefined}
                        isLoadingMore={previewLoading}
                    />
                )}
            </FlexCol>

            {/* Version History */}
            <FlexCol $gap={8}>
                <SectionLabel>Version History</SectionLabel>
                {versionsLoading && (
                    <div style={{ padding: 8, color: appColors.neutral.w500, fontSize: 12 }}>Loading versions...</div>
                )}
                {versions && versions.length === 0 && (
                    <div style={{ padding: 8, color: appColors.neutral.w500, fontSize: 12 }}>No versions</div>
                )}
                {versions && versions.length > 0 && (
                    <DatasetListTable>
                        <thead>
                            <tr>
                                <DatasetListTh>Version</DatasetListTh>
                                <DatasetListTh>Status</DatasetListTh>
                                <DatasetListTh>Started</DatasetListTh>
                                <DatasetListTh>Size</DatasetListTh>
                                <DatasetListTh style={{ textAlign: 'right' }}></DatasetListTh>
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map((version) => {
                                const isCurrent = version.id === dataset.currentVersionId;
                                const isSelected = version.id === selectedVersionId;
                                const rowBackground = isCurrent
                                    ? appColors.primary.w50
                                    : isSelected
                                    ? appColors.neutral.w100
                                    : undefined;
                                return (
                                    <DatasetRow
                                        key={version.id}
                                        onClick={() => setSelectedVersionId(version.id)}
                                        style={rowBackground ? { background: rowBackground } : undefined}
                                    >
                                        <DatasetListTd>
                                            <code style={{ fontSize: 11 }}>{version.id.substring(0, 12)}</code>
                                        </DatasetListTd>
                                        <DatasetListTd>{version.status}</DatasetListTd>
                                        <DatasetListTd title={version.startedAt.toLocaleString()}>
                                            {timeAgo(version.startedAt)}
                                        </DatasetListTd>
                                        <DatasetListTd>{formatBytes(version.totalSizeBytes)}</DatasetListTd>
                                        <DatasetListTd style={{ textAlign: 'right' }}>
                                            {isCurrent ? (
                                                <VersionBadge $current>Current</VersionBadge>
                                            ) : (
                                                version.status === 'completed' && (
                                                    <ActionButton
                                                        disabled={promoteVersion.isLoading}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePromote(version.id);
                                                        }}
                                                    >
                                                        {promoteVersion.isLoading &&
                                                        promoteVersion.variables === version.id
                                                            ? 'Promoting…'
                                                            : 'Promote'}
                                                    </ActionButton>
                                                )
                                            )}
                                        </DatasetListTd>
                                    </DatasetRow>
                                );
                            })}
                        </tbody>
                    </DatasetListTable>
                )}
            </FlexCol>
        </FlexCol>
    );
}

// ── Root ─────────────────────────────────────────────────────────────

export function DatasetCatalog() {
    const { data: datasets, isLoading, error } = useDatasets();
    const [selectedDataset, setSelectedDataset] = useState<DatasetRepresentation | null>(null);

    if (selectedDataset) {
        return (
            <div style={{ padding: 16 }}>
                <DatasetDetailView dataset={selectedDataset} onBack={() => setSelectedDataset(null)} />
            </div>
        );
    }

    return (
        <div style={{ padding: 16 }}>
            <FlexCol $gap={16}>
                <FlexCol $gap={4}>
                    <h3 style={{ margin: 0 }}>Datasets</h3>
                    <p style={{ margin: 0, color: appColors.neutral.w700, fontSize: 12 }}>
                        Browse datasets produced by pipelines. Click a dataset to preview its data and manage versions.
                    </p>
                </FlexCol>

                {isLoading && (
                    <div style={{ padding: 20, color: appColors.neutral.w500, fontSize: 12, textAlign: 'center' }}>
                        Loading datasets...
                    </div>
                )}

                {error instanceof Error && (
                    <div style={{ padding: 20, color: appColors.error.w500, fontSize: 12, textAlign: 'center' }}>
                        Failed to load datasets
                    </div>
                )}

                {datasets && <DatasetListView datasets={datasets} onSelect={setSelectedDataset} />}
            </FlexCol>
        </div>
    );
}
